import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Paper,
  IconButton,
  Slide,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/material/styles';

// Transition for dialog
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Styled Dialog Title with gradient and Close button
const StyledDialogTitle = ({ children, onClose }) => (
  <DialogTitle
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    }}
  >
    <Typography variant="h6" component="div">
      {children}
    </Typography>
    {onClose ? (
      <IconButton onClick={onClose}>
        <CloseIcon />
      </IconButton>
    ) : null}
  </DialogTitle>
);

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  borderRadius: theme.spacing(1),
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const StudentDetailsDialog = ({ open, onClose, students }) => {
  if (!students) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      TransitionComponent={Transition}
    >
      <StyledDialogTitle onClose={onClose}>
        Learner Performance Summary
      </StyledDialogTitle>

      <DialogContent dividers>
        {students.map((student, index) => (
          <Paper
            key={index}
            sx={{
              mb: 3,
              p: 3,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: 'none',
              transition: 'transform 0.2s ease-in-out',
              '&:hover': {
                transform: 'scale(1.01)',
              },
            }}
            elevation={3}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle1">
                  <strong>Name:</strong> {student.firstName} {student.lastName}
                </Typography>
                <Typography variant="body2">
                  <strong>Email:</strong> {student.studentEmail}
                </Typography>
                <Typography variant="body2">
                  <strong>Score:</strong> {student.score}/{student.totalMarks}
                </Typography>
                <Typography variant="body2">
                  <strong>Attempted:</strong> {student.attemptedQuestions}/{student.totalQuestions}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2"><strong>Correct:</strong> {student.correctAnswers}</Typography>
                <Typography variant="body2"><strong>Wrong:</strong> {student.wrongAnswers}</Typography>
                <Typography variant="body2"><strong>Unattempted:</strong> {student.unattemptedQuestions}</Typography>
              </Grid>
            </Grid>

            <StyledAccordion sx={{ mt: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>Analytic Data</Typography>
              </AccordionSummary>
              <AccordionDetails>
                {student.analyticData?.map((item, idx) => (
                  <Typography key={idx} variant="body2">
                    <strong>{item.key}:</strong> {item.data}
                  </Typography>
                ))}
              </AccordionDetails>
            </StyledAccordion>

            <StyledAccordion sx={{ mt: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>Domain Performance</Typography>
              </AccordionSummary>
              <AccordionDetails>
                {student.domainPerformance?.map((dp, idx) => (
                  <Typography key={idx} variant="body2">
                    <strong>{dp.domainName}:</strong> {dp.correctAnswers}/{dp.totalQuestion} Correct
                  </Typography>
                ))}
              </AccordionDetails>
            </StyledAccordion>
          </Paper>
        ))}
      </DialogContent>
    </Dialog>
  );
};

export default StudentDetailsDialog;
